/**
 * ATT FAKTURERA — skärmen kunden betalar 119 kr i månaden för.
 * Prototypens data-screen="fakturera".
 *
 * DET HÄR ÄR RÄTTELSEN AV PROTOTYPEN. Den visar ETT tal, "84 500 kr", och lägger
 * i det både de två attesterade jobben OCH ett tredje som står "attestera
 * först". Beslut 05 förbjuder just det: attesterat och oattesterat redovisas var
 * för sig och summeras ALDRIG ihop tyst. Kontraktet har byggt in regeln —
 * InvoiceBasisResponse har `approved` och `pending` som två SKILDA
 * InvoiceBasisTotals och INGEN gemensam total — och skärmen följer typen:
 * <Sifferpar> har plats för exakt två block och ingen summarad, och varje
 * arbetsorderkort skriver ut BÅDA talen när båda finns. Ett blandat tal får
 * chefen att skicka en faktura på timmar ingen godkänt.
 *
 * TALET ÄR RÄKNAT, INTE UPPSKATTAT. Servern räknar ur de FRYSTA satserna på
 * varje rad (beslut 15) med rundningsreglerna i company/amounts.ts — aldrig ur
 * dagens prislista. Ändrar chefen sitt timpris i morgon ändras inte det här
 * underlaget, och gamla fakturor kan inte börja röra på sig.
 *
 * ALLA BELOPP ÄR EX MOMS OCH FÖRE ROT (shared/types.ts, första stycket).
 * Momssatsen följer med i svaret och skrivs ut, men Klartid lägger aldrig på
 * momsen själv och räknar aldrig ROT-avdraget.
 *
 * EXPORTEN LIGGER ALDRIG BAKOM BETALVÄGG (beslut 11). INVOICE_BASIS är
 * locked_mode 'allowed', så CSV-knappen fungerar även i låsläge — det är
 * SYNC_PUSH som är stängd, alltså fakturamärkningen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { INVOICE_BASIS, SYNC_PUSH } from '@delat/routes';
import { asMillis } from '@delat/types';
import type {
  InvoiceBasisLine,
  InvoiceBasisResponse,
  InvoiceBasisTotals,
  InvoiceBasisWorkOrder,
  InvoiceRunId,
  InvoicedFilter,
  MaterialUnit,
  OpId,
  Vehicle,
  WorkOrderId,
} from '@delat/types';
import { granska, klienten, enhetsId, nyUlid } from '../api';
import type { Klartidfel } from '../api';
import { granskaFakturaunderlag, granskaSyncPush, somKlartidfel } from '../api/granskare/laget';
import { useInloggad } from '../app/session';
import { visaMil, visaMinuter, visaOren, visaTusendel } from '../app/datum';
import {
  Bricka,
  Fakturarad,
  Felruta,
  Knapp,
  Laddar,
  Listrad,
  Orderkort,
  Segment,
  Sifferblock,
  Sifferpar,
  Tomtext,
  Yta,
} from '../ui';
import { PERIODVALEN, periodFor } from './LagetData';
import type { Periodval } from './LagetData';
import type { Segmentval } from '../ui';

/* == Ordval ============================================================== */

const FILTERVALEN: readonly Segmentval<InvoicedFilter>[] = [
  { varde: 'unbilled', etikett: 'Ofakturerat' },
  { varde: 'billed', etikett: 'Fakturerat' },
  { varde: 'all', etikett: 'Allt' },
];

const ENHETSTEXT: Readonly<Record<MaterialUnit, string>> = {
  pcs: 'st',
  m: 'm',
  m2: 'm2',
  m3: 'm3',
  kg: 'kg',
  l: 'l',
  pkg: 'förp',
};

/** Informationsfält. Påverkar aldrig milbeloppet (shared/types.ts). */
const FORDONSTEXT: Readonly<Record<Vehicle, string>> = {
  own_car: 'egen bil',
  company_car: 'firmabil',
};

/* == Raderna =========================================================== */

const radtitel = (rad: InvoiceBasisLine): string => {
  switch (rad.kind) {
    case 'work':
      return rad.task_name;
    case 'material':
      return rad.description;
    case 'expense':
      return rad.description;
    case 'mileage':
      return 'Resa';
  }
};

const radbi = (rad: InvoiceBasisLine): string => {
  switch (rad.kind) {
    case 'work':
      return `${rad.work_date} · ${rad.account_name} · ${visaMinuter(rad.minutes)}`;
    case 'material':
      return `${rad.work_date} · ${visaTusendel(rad.quantity_milli)} ${ENHETSTEXT[rad.unit]}`;
    case 'expense':
      return `${rad.work_date} · vidarefaktureras`;
    case 'mileage':
      return `${rad.work_date} · ${visaMil(rad.meters)} · ${FORDONSTEXT[rad.vehicle]}`;
  }
};

const radunder = (rad: InvoiceBasisLine): string | undefined => {
  const noter: string[] = [];
  if (!rad.week_approved) noter.push('Väntar på attest');
  if (rad.invoice_run_id !== null) noter.push('Fakturamärkt');
  if (rad.kind === 'work' && Number(rad.rot_hundredths) > 0) noter.push('ROT');
  return noter.length === 0 ? undefined : noter.join(' · ');
};

/* == Summorna per hink =================================================== */

const arTom = (s: InvoiceBasisTotals): boolean => Number(s.total_ore) === 0;

const Summor = ({
  rubrik,
  summor,
}: {
  readonly rubrik: string;
  readonly summor: InvoiceBasisTotals;
}) => (
  // EX MOMS EN GÅNG, I RÄKNINGEN: alla rader i hinken är ex moms (shared/types.ts
  // första stycket), och att skriva ut det per rad hade gjort fyra rader till
  // fyra meningar.
  <Yta rubrik={rubrik} rakning={`${visaOren(summor.total_ore)} ex moms`} tyst>
    {Number(summor.work_ore) !== 0 && (
      <Fakturarad titel="Arbete" varde={visaOren(summor.work_ore)} />
    )}
    {Number(summor.material_ore) !== 0 && (
      <Fakturarad titel="Material" varde={visaOren(summor.material_ore)} />
    )}
    {Number(summor.expense_ore) !== 0 && (
      <Fakturarad titel="Vidarefakturerade utlägg" varde={visaOren(summor.expense_ore)} />
    )}
    {Number(summor.mileage_ore) !== 0 && (
      <Fakturarad titel="Mil" varde={visaOren(summor.mileage_ore)} />
    )}
    {/* Beslut 05: totalen hör hemma INUTI en hink, aldrig under båda. */}
    <Fakturarad titel={`Summa ${rubrik.toLowerCase()}`} varde={visaOren(summor.total_ore)} total />
  </Yta>
);

/* == CSV ================================================================= */

const csvFalt = (varde: string): string =>
  /[";\r\n]/.test(varde) ? `"${varde.replaceAll('"', '""')}"` : varde;

/** Ören -> "1295,00". Svensk decimalkomma, så Excel läser talet som ett tal. */
const kronor = (oren: number): string => (oren / 100).toFixed(2).replace('.', ',');

const antalMed = (varde: number, delare: number, decimaler: number): string =>
  (varde / delare).toFixed(decimaler).replace('.', ',');

const KOLUMNER = [
  'kund',
  'arbetsorder',
  'arbetsorder_id',
  'datum',
  'slag',
  'beskrivning',
  'antal',
  'enhet',
  'a_pris_kr_ex_moms',
  'belopp_kr_ex_moms',
  'attesterad',
  'fakturamarkt',
  'rot_hundradels_procent',
  'moms_hundradels_procent',
] as const;

const csvRad = (
  order: InvoiceBasisWorkOrder,
  rad: InvoiceBasisLine,
  momsHundradelar: number,
): readonly string[] => {
  const gemensamt = [
    order.customer_name,
    order.description,
    order.work_order_id as string,
    rad.work_date as string,
  ];
  const svans = [
    rad.week_approved ? 'ja' : 'nej',
    rad.invoice_run_id === null ? 'nej' : 'ja',
    rad.kind === 'work' ? String(Number(rad.rot_hundredths)) : '0',
    String(momsHundradelar),
  ];
  switch (rad.kind) {
    case 'work':
      return [
        ...gemensamt,
        'arbete',
        `${rad.task_name} (${rad.account_name})`,
        antalMed(Number(rad.minutes), 60, 2),
        'h',
        kronor(Number(rad.rate_ore_per_hour)),
        kronor(Number(rad.amount_ore)),
        ...svans,
      ];
    case 'material':
      return [
        ...gemensamt,
        'material',
        rad.description,
        antalMed(Number(rad.quantity_milli), 1000, 3),
        ENHETSTEXT[rad.unit],
        kronor(Number(rad.unit_price_ore)),
        kronor(Number(rad.amount_ore)),
        ...svans,
      ];
    case 'expense':
      return [
        ...gemensamt,
        'utlagg',
        rad.description,
        '1',
        'st',
        kronor(Number(rad.amount_ore)),
        kronor(Number(rad.amount_ore)),
        ...svans,
      ];
    case 'mileage':
      return [
        ...gemensamt,
        'mil',
        `Resa (${FORDONSTEXT[rad.vehicle]})`,
        antalMed(Number(rad.meters), 10000, 1),
        'mil',
        kronor(Number(rad.rate_ore_per_10km)),
        kronor(Number(rad.amount_ore)),
        ...svans,
      ];
  }
};

/**
 * Underlaget som CSV. Kontraktet har ingen exportrutt — "Klienten gör CSV:n ur
 * svaret" (shared/routes.ts, INVOICE_BASIS) — så det är HÄR filen blir till.
 * Semikolon och decimalkomma därför att den öppnas i svensk Excel.
 */
const byggCsv = (svar: InvoiceBasisResponse): string => {
  const moms = Number(svar.vat_rate_hundredths);
  const rader = svar.work_orders.flatMap((order) =>
    order.lines.map((rad) => csvRad(order, rad, moms).map(csvFalt).join(';')),
  );
  return [KOLUMNER.join(';'), ...rader].join('\r\n');
};

/**
 * Byte order mark, skriven som ESCAPE och inte som tecken: ett osynligt tecken
 * i källkoden är ett tecken nästa läsare tar bort av misstag. Utan den tolkar
 * svensk Excel filen som ANSI och å, ä och ö går sönder i varje beskrivning.
 */
const BOM = '\uFEFF';

const laddaNer = (filnamn: string, innehall: string): void => {
  const blobb = new Blob([BOM + innehall], { type: 'text/csv;charset=utf-8' });
  const adress = URL.createObjectURL(blobb);
  const lank = document.createElement('a');
  lank.href = adress;
  lank.download = filnamn;
  document.body.append(lank);
  lank.click();
  lank.remove();
  URL.revokeObjectURL(adress);
};

/* == Skärmen ============================================================= */

type Underlagslage =
  | { readonly status: 'laddar' }
  | { readonly status: 'fel'; readonly fel: Klartidfel }
  | { readonly status: 'klar'; readonly svar: InvoiceBasisResponse };

export const AttFakturera = () => {
  const { firmaId, arChef, arLast } = useInloggad();
  const [period, sattPeriod] = useState<Periodval>('12v');
  const [filter, sattFilter] = useState<InvoicedFilter>('unbilled');
  const [omgang, sattOmgang] = useState(0);
  const [oppen, sattOppen] = useState<WorkOrderId | null>(null);
  const [lage, sattLage] = useState<Underlagslage>({ status: 'laddar' });
  const [bekraftar, sattBekraftar] = useState(false);
  const [arbetar, sattArbetar] = useState(false);
  const [pushfel, sattPushfel] = useState<Klartidfel | null>(null);

  const intervall = useMemo(() => periodFor(period), [period]);

  /**
   * BESLUT 09: både op_id OCH körningens id myntas när användaren trycker och
   * återanvänds vid varje omsändning. Körningens id är dessutom operationens
   * ENDA skydd mot en andra stämpling — den bär ingen base_version, så ett id
   * som redan finns ger SYNC_CONFLICT (shared/types.ts, SyncOpBody). Ett nytt id
   * per försök hade gjort ett tappat nät till två fakturamärkningar.
   */
  const korning = useRef<{ readonly opId: OpId; readonly runId: InvoiceRunId } | null>(null);

  useEffect(() => {
    const styrning = new AbortController();
    let aktuell = true;
    sattLage({ status: 'laddar' });

    void (async () => {
      const svar = await klienten.anrop(
        INVOICE_BASIS,
        {
          params: { company_id: firmaId },
          data: {
            from_date: intervall.from_date,
            to_date: intervall.to_date,
            invoiced_filter: filter,
          },
          signal: styrning.signal,
        },
        granskaFakturaunderlag,
      );
      if (!aktuell) return;
      sattLage(svar.ok ? { status: 'klar', svar: svar.varde } : { status: 'fel', fel: svar.fel });
    })();

    return () => {
      aktuell = false;
      styrning.abort();
    };
  }, [firmaId, intervall, filter, omgang]);

  const markera = useCallback(() => {
    if (arbetar) return;
    sattArbetar(true);
    sattPushfel(null);

    void (async () => {
      const befintlig = korning.current;
      const nyckel = befintlig ?? {
        opId: granska.opId(nyUlid(), 'op_id'),
        runId: granska.invoiceRunId(nyUlid(), 'invoice_run.id'),
      };
      korning.current = nyckel;

      const svar = await klienten.anrop(
        SYNC_PUSH,
        {
          params: { company_id: firmaId },
          data: {
            device_id: enhetsId(),
            ops: [
              {
                op_id: nyckel.opId,
                client_time: asMillis(Date.now()),
                kind: 'invoice_run',
                id: nyckel.runId,
                from_date: intervall.from_date,
                to_date: intervall.to_date,
                customer_id: null,
                work_order_id: null,
                note: null,
              },
            ],
          },
        },
        granskaSyncPush,
      );

      if (!svar.ok) {
        // Helanropsfel rör aldrig utkorgen: nyckeln ligger kvar och samma tryck
        // skickar samma op_id och samma körningsid igen.
        sattPushfel(svar.fel);
        sattArbetar(false);
        return;
      }
      const resultat = svar.varde.results[0];
      if (resultat !== undefined && resultat.status === 'failed') {
        sattPushfel(somKlartidfel(resultat.error));
        sattArbetar(false);
        return;
      }
      korning.current = null;
      sattBekraftar(false);
      sattArbetar(false);
      sattOmgang((n) => n + 1);
    })();
  }, [arbetar, firmaId, intervall]);

  const svar = lage.status === 'klar' ? lage.svar : null;
  const kanMarkera =
    arChef && !arLast && filter !== 'billed' && svar !== null && !arTom(svar.approved);

  return (
    <>
      <Segment val={PERIODVALEN} valt={period} onValj={sattPeriod} etikett="Period" />
      <Segment val={FILTERVALEN} valt={filter} onValj={sattFilter} etikett="Fakturastatus" />

      {pushfel !== null && (
        <Felruta
          fel={pushfel}
          atgard={
            <Knapp variant="piller-mini" onClick={() => sattPushfel(null)}>
              Stäng
            </Knapp>
          }
        />
      )}

      {lage.status === 'laddar' && <Laddar rader={5} />}

      {lage.status === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller" onClick={() => sattOmgang((n) => n + 1)}>
              Försök igen
            </Knapp>
          }
        />
      )}

      {svar !== null && (
        <>
          <Sifferpar
            vanster={
              <Sifferblock
                varde={visaOren(svar.approved.total_ore)}
                under="Attesterat, ex moms"
              />
            }
            hoger={
              <Sifferblock
                varde={visaOren(svar.pending.total_ore)}
                under="Väntar på attest, ex moms"
              />
            }
          />

          <Tomtext>
            De två talen läggs aldrig ihop. Bara attesterad tid kan faktureras, och
            bara attesterade rader kan fakturamärkas — det andra talet är hur mycket
            som ligger och väntar på dig i Attestera.
          </Tomtext>

          {svar.work_orders.length === 0 && (
            <Tomtext>
              {filter === 'unbilled'
                ? 'Inget ofakturerat i perioden. Välj en längre period eller en annan fakturastatus.'
                : 'Inga rader i perioden.'}
            </Tomtext>
          )}

          {svar.work_orders.map((order) => {
            const arbetsminuter = order.lines.reduce(
              (s, rad) => (rad.kind === 'work' ? s + Number(rad.minutes) : s),
              0,
            );
            const attesterat = Number(order.approved.total_ore);
            const vantar = Number(order.pending.total_ore);
            const fot =
              vantar === 0
                ? `${visaOren(attesterat)} attesterat, ex moms`
                : attesterat === 0
                  ? `${visaOren(vantar)} väntar på attest, ex moms`
                  : `${visaOren(attesterat)} attesterat · ${visaOren(vantar)} väntar, ex moms`;
            const bricka =
              attesterat === 0 && vantar === 0 ? (
                <Bricka ton="neutral">Inga belopp</Bricka>
              ) : vantar === 0 ? (
                <Bricka ton="klar">Attesterat</Bricka>
              ) : attesterat === 0 ? (
                <Bricka ton="pagaende">Väntar attest</Bricka>
              ) : (
                <Bricka ton="pagaende">Delvis attesterat</Bricka>
              );

            return (
              <div key={order.work_order_id}>
                <Orderkort
                  adress={order.description}
                  meta={`${order.customer_name} · ${visaMinuter(arbetsminuter)}`}
                  fot={fot}
                  bricka={bricka}
                  onClick={() =>
                    sattOppen((n) => (n === order.work_order_id ? null : order.work_order_id))
                  }
                />
                {oppen === order.work_order_id && (
                  <>
                    {!arTom(order.approved) && (
                      <Summor rubrik="Attesterat" summor={order.approved} />
                    )}
                    {!arTom(order.pending) && (
                      <Summor rubrik="Väntar på attest" summor={order.pending} />
                    )}
                    <Yta rubrik="Rader" rakning={`${order.lines.length} st`} tyst>
                      {order.lines.map((rad) => (
                        <Listrad
                          key={`${rad.kind}-${rad.id}`}
                          titel={radtitel(rad)}
                          bi={radbi(rad)}
                          under={radunder(rad)}
                          varde={visaOren(rad.amount_ore)}
                        />
                      ))}
                    </Yta>
                  </>
                )}
              </div>
            );
          })}

          {svar.lines_truncated && (
            <Tomtext>
              Fler rader än vad som får plats träffades av perioden. Summorna ovan är
              ändå kompletta — det är bara listan som är avkortad. Välj en kortare
              period för att se alla rader.
            </Tomtext>
          )}

          <div style={{ marginTop: '0.8rem' }}>
            <Knapp
              variant={kanMarkera ? 'stor-tyst' : 'stor'}
              disabled={svar.work_orders.length === 0}
              onClick={() =>
                laddaNer(
                  `klartid-underlag-${svar.from_date}-${svar.to_date}.csv`,
                  byggCsv(svar),
                )
              }
            >
              Exportera CSV
            </Knapp>
          </div>

          {kanMarkera && (
            <div style={{ marginTop: '0.5rem' }}>
              <Knapp
                disabled={arbetar}
                onClick={() => (bekraftar ? markera() : sattBekraftar(true))}
              >
                {bekraftar
                  ? `Bekräfta: markera ${visaOren(svar.approved.total_ore)} ex moms som fakturerat`
                  : 'Markera som fakturerat'}
              </Knapp>
            </div>
          )}

          {bekraftar && (
            <Tomtext>
              Märkningen sätts på varje ATTESTERAD rad i perioden som inte redan bär en.
              En märkt rad är låst för alltid — enda vägen tillbaka är att ta bort hela
              fakturakörningen. Oattesterade rader rör den inte.
            </Tomtext>
          )}

          {!bekraftar && (
            <Tomtext>
              {arChef
                ? 'Räknat ur rapporterade timmar gånger det pris som frystes på raden när den sparades — inte ur dagens prislista, och inte uppskattat. Alla belopp är ex moms och före ROT.'
                : 'Du läser och exporterar. Fakturamärkningen gör chefen. Alla belopp är ex moms och före ROT.'}
            </Tomtext>
          )}
        </>
      )}
    </>
  );
};
